import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdPhotoCamera, MdSearch } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import GymIdMemberSearch from '../../components/receptionist/GymIdMemberSearch';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import { getGymMemberId } from '../admin/members/memberData';
import {
  fetchAttendance,
  fetchAttendancePage,
  fetchMembersPage,
  fetchTrainers,
  createAttendance,
  checkoutAttendance,
  findOpenAttendance,
} from '../../services/receptionist/receptionistApi';
import { useServerPagedList } from '../../hooks/useServerPagedList';
import TablePagination from '../../components/shared/TablePagination';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../admin/attendance/attendance.css';
import '../receptionist/members/members.css';
import {
  printAttendanceList,
  exportAttendanceCsv,
  exportAttendancePdf,
  exportAttendanceExcel,
  exportAttendance,
} from './exportAttendance';
import { instantLoadState } from '../../utils/instantLoad';
import { dedupeAttendance, dedupeMembers, dedupePeople } from '../../utils/dedupeList';

function formatTimeNow() {
  return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function attendanceTypeLabel(record) {
  return (record.personType || 'member') === 'trainer' ? 'Trainer' : 'Member';
}

function attendanceStatusLabel(record) {
  return record.status === 'in-gym' ? 'In Gym' : 'Completed';
}

function resolveAttendanceGymId(record) {
  const personType = record.personType || 'member';
  if (personType !== 'member') return '—';
  return record.gymId || '—';
}

function resolveAttendanceBranch(record) {
  return record.branch || '—';
}

function Attendance() {
  const toast = usePanelToast();
  const trainerBootstrap = instantLoadState('trainers');
  const [trainers, setTrainers] = useState(trainerBootstrap.initialData);
  const [pickerMembers, setPickerMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState('member');
  const [modalStep, setModalStep] = useState('select');
  const [scanPreview, setScanPreview] = useState(null);
  const [selectedPersonName, setSelectedPersonName] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [detectedAction, setDetectedAction] = useState('check-in');
  const [scanError, setScanError] = useState('');
  const [modalGymIdQuery, setModalGymIdQuery] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    type: '',
    gymId: '',
    name: '',
    branch: '',
    date: '',
    checkIn: '',
    checkOut: '',
    status: '',
  });

  const fetchPage = useCallback((params) => fetchAttendancePage(params), []);
  const {
    rows: allRecords,
    total,
    page,
    setPage,
    totalPages,
    from,
    to,
    loading,
    error,
    setError,
    reload,
    nextPage,
    prevPage,
  } = useServerPagedList({ fetcher: fetchPage, search, filters });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const trainerData = await fetchTrainers();
        if (!cancelled) setTrainers(dedupePeople(trainerData));
      } catch {
        // keep bootstrap trainers
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Server-search members for the attendance modal picker
  useEffect(() => {
    if (!showModal || attendanceMode !== 'member') return undefined;
    const q = modalGymIdQuery.trim();
    if (!q || selectedPersonName) return undefined;
    const timer = setTimeout(async () => {
      try {
        const result = await fetchMembersPage({ search: q, pageSize: 20 });
        setPickerMembers(dedupeMembers(result.rows || []));
      } catch {
        setPickerMembers([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [modalGymIdQuery, showModal, attendanceMode, selectedPersonName]);

  const trainerOptions = useMemo(
    () => trainers.filter((t) => t.status === 'active').map((t) => t.name),
    [trainers],
  );

  const trainerIdByName = useMemo(() => {
    const map = new Map();
    trainers.forEach((t) => map.set(t.name, t.id));
    return map;
  }, [trainers]);

  const personLabel = attendanceMode === 'trainer' ? 'Trainer' : 'Member';
  const modalTitle = attendanceMode === 'trainer' ? 'Mark Trainer Attendance' : 'Mark Member Attendance';

  const filteredRecords = allRecords;

  const handleExport = async (exporterFn) => {
    try {
      const all = await fetchAttendance();
      const rows = dedupeAttendance(all).map((r) => {
        const personType = r.personType || 'member';
        return {
          ...r,
          type: personType === 'trainer' ? 'Trainer' : 'Member',
          gymId: personType === 'member' ? (r.gymId || '—') : '—',
          statusLabel: r.status === 'in-gym' ? 'In Gym' : 'Completed',
        };
      });
      exporterFn(rows);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const resetScanState = useCallback(() => {
    stopCamera();
    if (scanPreview) URL.revokeObjectURL(scanPreview);
    setScanPreview(null);
    setSelectedPersonName('');
    setSelectedMember(null);
    setModalGymIdQuery('');
    setPickerMembers([]);
    setDetectedAction('check-in');
    setScanError('');
    setModalStep('select');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [scanPreview, stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const openModal = (mode) => {
    setAttendanceMode(mode);
    resetScanState();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetScanState();
  };

  const handleSelectPerson = async (name, member = null) => {
    setSelectedPersonName(name);
    setSelectedMember(member);
    setScanError('');
    if (!name) {
      setDetectedAction('check-in');
      return;
    }
    try {
      const personType = attendanceMode;
      const memberId = personType === 'member' ? member?.id : null;
      const trainerId = personType === 'trainer' ? trainerIdByName.get(name) : null;
      const open = await findOpenAttendance(memberId, trainerId, personType);
      setDetectedAction(open ? 'check-out' : 'check-in');
    } catch {
      setDetectedAction('check-in');
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setScanError('Camera is not supported in this browser. Try Chrome or Edge on HTTPS/localhost.');
      return;
    }

    setScanError('');
    stopCamera();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setModalStep('camera');

      requestAnimationFrame(async () => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch {
            setScanError('Could not start camera preview.');
          }
        }
      });
    } catch {
      setScanError('Could not open camera. Allow camera permission in your browser settings.');
    }
  };

  const handleScanClick = () => {
    if (!selectedPersonName) {
      setScanError(`Please select a ${personLabel.toLowerCase()} first.`);
      return;
    }
    startCamera();
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setScanError('Camera is not ready yet. Wait a moment and try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) {
        setScanError('Could not capture photo. Please try again.');
        return;
      }
      if (scanPreview) URL.revokeObjectURL(scanPreview);
      setScanPreview(URL.createObjectURL(blob));
      stopCamera();
      setScanError('');
      setModalStep('confirm');
    }, 'image/jpeg', 0.92);
  };

  const handlePhotoFallback = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (scanPreview) URL.revokeObjectURL(scanPreview);
    setScanPreview(URL.createObjectURL(file));
    setScanError('');
    stopCamera();
    setModalStep('confirm');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleBackToSelect = () => {
    stopCamera();
    if (scanPreview) URL.revokeObjectURL(scanPreview);
    setScanPreview(null);
    setScanError('');
    setModalStep('select');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleConfirm = async () => {
    if (!selectedPersonName) return;

    const time = formatTimeNow();
    const personType = attendanceMode;
    const memberId = personType === 'member' ? selectedMember?.id : null;
    const trainerId = personType === 'trainer' ? trainerIdByName.get(selectedPersonName) : null;

    try {
      if (detectedAction === 'check-in') {
        await createAttendance({
          personType,
          memberId,
          trainerId,
          checkIn: time,
          status: 'in-gym',
        });
        toast.showAdded('Attendance');
      } else {
        const open = await findOpenAttendance(memberId, trainerId, personType);
        if (open) {
          await checkoutAttendance(open.id, time);
          toast.showUpdated('Attendance');
        }
      }
      closeModal();
      await reload();
    } catch (err) {
      const message = err.message || 'Failed to save attendance';
      setScanError(message);
      toast.showError(message);
    }
  };

  return (
    <div>
      <PageHeader title="Attendance" subtitle="Mark attendance and search records by Serial No" variant="banner">
        <button type="button" className="owner-btn owner-btn-primary" onClick={() => openModal('member')}>
          Mark Member Attendance
        </button>
        <button type="button" className="owner-btn owner-btn-primary" onClick={() => openModal('trainer')} style={{ marginLeft: 8 }}>
          Mark Trainer Attendance
        </button>
      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading attendance...</p>}

      <div className="rec-member-toolbar">
        <div className="rec-member-toolbar-left">
          <div className="rec-member-search-field">
            <MdSearch className="rec-member-search-icon" />
            <input
              type="text"
              className="owner-form-input rec-member-search-input"
              placeholder="Search by serial no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(printAttendanceList)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(exportAttendanceCsv)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(exportAttendancePdf)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => handleExport(exportAttendanceExcel)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => handleExport(exportAttendance)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card owner-mb-28">
        <h2 className="owner-card-title">Attendance Records</h2>

        <div className="owner-table-wrap">
          <table className="owner-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Type"
                  filterKey="type"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={['Member', 'Trainer']}
                  allLabel="All Types"
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="gymId"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Check In"
                  filterKey="checkIn"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search check in..."
                />
                <ColumnFilterHeader
                  label="Check Out"
                  filterKey="checkOut"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search check out..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={['In Gym', 'Completed']}
                  allLabel="All Status"
                />
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length > 0 ? (
                filteredRecords.map((r) => {
                  const personType = r.personType || 'member';
                  const gymId = resolveAttendanceGymId(r);
                  const isInGym = r.status === 'in-gym';
                  return (
                    <tr key={`${r.id}-${r.member}-${r.date}-${r.checkIn}`}>
                      <td>
                        <span className={`owner-badge ${personType === 'trainer' ? 'owner-badge-info' : 'owner-badge-success'}`}>
                          {attendanceTypeLabel(r)}
                        </span>
                      </td>
                      <td className="rec-member-gym-id">{gymId}</td>
                      <td>{r.member}</td>
                      <td>{resolveAttendanceBranch(r)}</td>
                      <td>{r.date}</td>
                      <td>{r.checkIn}</td>
                      <td>{r.checkOut || '—'}</td>
                      <td>
                        <span className={`owner-badge ${isInGym ? 'owner-badge-info' : 'owner-badge-success'}`}>
                          {attendanceStatusLabel(r)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text">
                    {search.trim() ? 'No attendance records match this serial no.' : 'No attendance records found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>

      {showModal && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div
            className="subscription-modal admin-form-modal rec-attendance-scan-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mark-attendance-title"
          >
            <div className="subscription-modal-header">
              <h2 id="mark-attendance-title" className="subscription-modal-title">{modalTitle}</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>

            {modalStep === 'select' && (
              <div className="rec-attendance-scan-body">
                {attendanceMode === 'member' ? (
                  <GymIdMemberSearch
                    id="attendance-modal-gym-id"
                    label="Serial No"
                    className="owner-form-group"
                    query={modalGymIdQuery}
                    onQueryChange={setModalGymIdQuery}
                    selectedName={selectedPersonName}
                    selectedGymId={selectedMember ? getGymMemberId(selectedMember) : ''}
                    onSelect={({ name, gymId, member }) => {
                      handleSelectPerson(name, member);
                      if (gymId) setModalGymIdQuery(gymId);
                    }}
                    members={pickerMembers}
                    placeholder="Search serial no, name, phone, or CNIC..."
                  />
                ) : (
                  <div className="owner-form-group">
                    <label className="owner-form-label" htmlFor="attendance-scan-trainer">Trainer</label>
                    <select
                      id="attendance-scan-trainer"
                      className="owner-form-select"
                      value={selectedPersonName}
                      onChange={(e) => handleSelectPerson(e.target.value)}
                    >
                      <option value="">Select trainer...</option>
                      {trainerOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <p className="rec-attendance-scan-hint">
                  {attendanceMode === 'member'
                    ? 'Search by Serial No, select the member, then tap Scan to open the camera.'
                    : 'Select a trainer, then tap Scan to open the camera.'}
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="rec-attendance-scan-file"
                  onChange={handlePhotoFallback}
                />

                {scanError && <p className="rec-attendance-scan-error">{scanError}</p>}

                <div className="subscription-form-actions">
                  <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="button" className="owner-btn owner-btn-primary" onClick={handleScanClick}>
                    <MdPhotoCamera style={{ marginRight: 8 }} />
                    Scan
                  </button>
                </div>
              </div>
            )}

            {modalStep === 'camera' && (
              <div className="rec-attendance-scan-body">
                <div className="rec-attendance-camera-wrap">
                  <video
                    ref={videoRef}
                    className="rec-attendance-camera-video"
                    autoPlay
                    playsInline
                    muted
                  />
                </div>
                <p className="rec-attendance-scan-hint">Position the member in frame, then capture the photo.</p>
                {scanError && <p className="rec-attendance-scan-error">{scanError}</p>}
                <div className="subscription-form-actions">
                  <button type="button" className="owner-btn owner-btn-secondary" onClick={handleBackToSelect}>
                    Back
                  </button>
                  <button type="button" className="owner-btn owner-btn-primary" onClick={capturePhoto}>
                    <MdPhotoCamera style={{ marginRight: 8 }} />
                    Capture Photo
                  </button>
                </div>
              </div>
            )}

            {modalStep === 'confirm' && (
              <div className="rec-attendance-confirm-body">
                <div className="rec-attendance-confirm-card">
                  {scanPreview ? (
                    <img src={scanPreview} alt={selectedPersonName} className="rec-attendance-confirm-photo" />
                  ) : (
                    <div className="rec-attendance-confirm-photo rec-attendance-confirm-photo-placeholder">
                      {selectedPersonName?.charAt(0)}
                    </div>
                  )}
                  <div className="rec-attendance-confirm-details">
                    <h3 className="rec-attendance-confirm-name">{selectedPersonName}</h3>
                    <p className="rec-attendance-confirm-id">
                      {attendanceMode === 'member' ? (selectedMember ? getGymMemberId(selectedMember) : '') : personLabel}
                    </p>
                    <span className={`owner-badge ${detectedAction === 'check-in' ? 'owner-badge-success' : 'owner-badge-info'}`}>
                      {detectedAction === 'check-in' ? 'Check-in' : 'Check-out'}
                    </span>
                  </div>
                </div>

                <div className="subscription-form-actions">
                  <button type="button" className="owner-btn owner-btn-secondary" onClick={handleBackToSelect}>
                    Cancel
                  </button>
                  <button type="button" className="owner-btn owner-btn-primary" onClick={handleConfirm}>
                    Confirm
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Attendance;
